import { Avatar, AvatarProps, Box, Button, Card, CardContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Palette } from 'Palette';
import { RADIUS, SHADOW } from 'DesignTokens';
import { determineNextTone } from './stylesToneUtils';

export const LogoContainer = styled(Box)<{ className?: string }>(({ className }) => ({
  display: 'flex',
  alignItems: 'center',
  className: `react-LogoContainer ${className || ''}`.trim(),
}));

interface StyledAvatarProps extends AvatarProps {
  tone?: keyof typeof Palette;
}

export const StyledAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<StyledAvatarProps>(({ tone, className }) => {
  const palette = tone ? Palette[tone] : undefined;
  return {
    backgroundColor: palette?.buttonBg ?? '#e0e0e0',
    color: palette?.buttonText ?? '#000000',
    className: `react-StyledAvatar ${className || ''}`.trim(),
  };
});

export const CardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone?: keyof typeof Palette; className?: string }>(({ tone = 'default', className }) => ({
  backgroundColor: Palette[tone].background,
  color: Palette[tone].text,
  borderRadius: RADIUS.cardContainer,
  overflow: 'hidden',
  padding: '0',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: SHADOW.cardContainerHover,
  },
  className: `react-CardContainer ${className || ''}`.trim(),
}));

export const ToneButton = styled(Button)<{ className?: string }>(({ className }) => ({
  backgroundColor: Palette.default.buttonBg,
  color: Palette.default.buttonText,
  borderRadius: RADIUS.button,
  padding: '0.5rem 1.5rem',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    opacity: 0.9,
    backgroundColor: Palette.default.buttonBg,
  },
  className: `react-ToneButton ${className || ''}`.trim(),
}));

export const StyledSectionCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: keyof typeof Palette }>(({ tone = 'default', className }) => ({
  backgroundColor: Palette[tone].background,
  color: Palette[tone].text,
  borderRadius: RADIUS.sectionCard,
  padding: '1.5rem',
  boxShadow: SHADOW.sectionCard,
  marginBottom: '2rem',
  className: `react-StyledSectionCard ${className || ''}`.trim(),
}));

export const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'tone' && prop !== 'hoverable',
})<{
  tone?: keyof typeof Palette;
  className?: string;
  hoverable?: boolean;
}>(({ tone = 'tone1', className, hoverable = false }) => ({
  backgroundColor: Palette[tone].background,
  color: Palette[tone].text,
  '&:hover': {
    backgroundColor: hoverable
      ? Palette[determineNextTone(tone)].background
      : Palette[tone].background,
    color: hoverable ? Palette[determineNextTone(tone)].text : Palette[tone].text,
    cursor: hoverable ? 'pointer' : 'default',
  },
  borderRadius: RADIUS.card,
  boxShadow: SHADOW.card,
  flex: 1,
  height: '100%',
  className: `react-StyledCard ${className || ''}`.trim(),
}));

export const StyledCardContent = styled(CardContent)<{ className?: string }>(({ className }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  className: `react-StyledCardContent ${className || ''}`.trim(),
}));

export const CardMeta = styled(Box)<{ className?: string }>(({ theme, className }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: theme.spacing(2),
  className: `react-CardMeta ${className || ''}`.trim(),
}));

export const AuthorBox = styled(Box)<{ className?: string }>(({ className }) => ({
  display: 'flex',
  alignItems: 'center',
  className: `react-AuthorBox ${className || ''}`.trim(),
}));

export const AuthorAvatar = styled(Avatar)<{ className?: string }>(({ theme, className }) => ({
  width: 32,
  height: 32,
  marginRight: theme.spacing(1),
  className: `react-AuthorAvatar ${className || ''}`.trim(),
}));
